<template>
  <b-modal
    centered
    hide-footer
    no-close-on-esc
    no-close-on-backdrop
    v-model="showModal"
    @close="closeModal"
    class="pa-4"
    :style="'width: 708px !important;'"
  >
    <template #modal-title>
      <h3 class="font-weight-bold">Share</h3>
    </template>
    <b-card
      no-body
      v-cloak
      class="rounded-0 border-0 mx-0 bg-light"
      @drop.prevent=""
      @dragover.prevent
    >
      <b-form @submit.prevent="submitFileShare">
        <b-card-text :style="'opacity: 1;'">
          <h6 class="font-weight-bold">
            Add an email to share your file
          </h6>
          <b-form-tags
            v-model="form.emails"
            no-outer-focus
            tag-pills
            class="h-auto mb-2 form-control"
          >
            <template
              v-slot="{
                tags,
                inputAttrs,
                inputHandlers,
                disabled,
                addTag,
                removeTag
              }"
            >
              <div class="mb-0">
                <div
                  class="mt-3"
                  style="font-size: 1.5rem;"
                  :class="{
                    'd-inline-block': form.emails.length,
                    'd-none': !form.emails.length
                  }"
                >
                  <b-form-tag
                    class="mr-1 mw-100 badge d-inline-flex align-items-baseline badge-pill border-gray"
                    v-for="(tag, i) in tags"
                    :key="i"
                    :title="tag"
                    :tag-pills="true"
                    :variant="'light'"
                    :disabled="loading"
                    :class="{
                      'border-danger':
                        checkInvalidTag(tag) ||
                        checkExcessTag(tag) ||
                        form.$hasError(`emails.${i}`)
                    }"
                    @remove="removeTag(tag)"
                  >
                    {{ tag }}
                  </b-form-tag>
                </div>
                <b-form-input
                  v-bind="inputAttrs"
                  v-on="inputHandlers"
                  autocomplete="on"
                  placeholder="Enter email..."
                  aria-describedby="shareFileFeedback"
                  class="border-0 p-0 mb-0"
                  :disabled="disabled || loading"
                  @keydown.enter.native="addTag()"
                ></b-form-input>
              </div>
            </template>
          </b-form-tags>
          <b-form-invalid-feedback
            v-show="tagErrors.message"
            id="shareFileFeedback"
            :class="{ 'd-block': tagErrors.message }"
          >
            {{ tagErrors.message }}
          </b-form-invalid-feedback>
          <b-form-invalid-feedback
            v-show="form.$getError('emails')"
            id="shareFileFeedback"
            :class="{ 'd-block': form.$getError('emails') }"
          >
            {{ form.$getError('emails') }}
          </b-form-invalid-feedback>

          <div class="ml-2">
            <b-icon class="mt-2" icon="exclamation-circle" variant="dark" />
            <span class="ml-1 mb-3">
              Press Enter on your keyboard to add an email address
            </span>
          </div>

          <div class="d-inline-block mt-3" style="font-size: 1.5rem;">
            <h6 class="font-weight-bold">
              Currently shared with
            </h6>
            <b-form-tag
              class="mr-1 mw-100 badge d-inline-flex align-items-baseline badge-secondary badge-pill"
              v-for="(tag, i) in currentlySharedWith"
              :key="i"
              :title="tag"
              :tag-pills="true"
              :variant="'secondary'"
              :disabled="loading"
              :class="{
                'border-warning': checkInvalidTag(tag) || checkExcessTag(tag)
              }"
              @remove="currentlySharedRevoke(tag)"
            >
              {{ tag }}
            </b-form-tag>
          </div>
        </b-card-text>

        <div class="my-4 mx-0">
          <b-container>
            <b-row>
              <b-col cols="12" md="5" class="mb-md-4 mb-3">
                <b-button
                  class="w-100 btn-outlined bg-white text-dark"
                  variant="outline-secondary"
                  :style="'height: 64px !important;'"
                  :disabled="loading"
                  @click="clear"
                >
                  Clear
                </b-button>
              </b-col>
              <b-col cols="12" md="2" class="mb-0"></b-col>
              <b-col cols="12" md="5" class="mb-md-4 mb-3">
                <b-button
                  type="submit"
                  class="w-100 h-auto float-right py-3"
                  variant="primary"
                  :style="'height: 64px !important;'"
                  :loading="loading"
                  :disabled="loading || !form.emails.length"
                >
                  Share
                </b-button>
              </b-col>
            </b-row>
          </b-container>
        </div>
      </b-form>
    </b-card>
    <b-modal v-model="showSubscribeModal" centered title="File sharing failed">
      <p class="my-4">
        You have reached your file sharing capacity. Subscribe to premium to
        share files to more people.
      </p>

      <template #modal-footer>
        <b-button variant="secondary" @click="showSubscribeModal = false">
          Go Back
        </b-button>
        <b-button
          variant="primary"
          @click="$router.replace('/premium/subscription')"
        >
          Subscribe now
        </b-button>
      </template>
    </b-modal>
  </b-modal>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { each, remove } from 'lodash'
import Form from '@/utils/form'

export default {
  props: {
    showShareFiles: {
      type: Boolean,
      default: false
    },
    selectedFiles: {
      type: Array
    },
    fileShares: {
      type: Array
    }
  },

  data() {
    return {
      loading: false,
      form: new Form({
        emails: []
      }),
      currentlySharedWith: [],
      validTags: [],
      invalidTags: [],
      maxTags: [],
      tagErrors: {
        message: null,
        error_code: null
      },
      search: '',
      showSubscribeModal: false
    }
  },

  computed: {
    ...mapState({
      user: state => state.auth.user
    }),
    isTradieUser() {
      return this.user ? this.user.user_type === 'tradie' : false
    },
    userSubcEmailLimit() {
      let limit = 0

      if (this.isTradieUser) {
        if (this.user.is_subscribed) {
          limit = 5
        } else {
          limit = 1
        }
      } else {
        if (this.user.is_subscribed) {
          limit = 50
        } else {
          limit = 5
        }
      }

      return limit
    },
    showModal: {
      get() {
        return this.showShareFiles
      },
      set() {
        this.$emit('closeShareFileModal')
      }
    }
  },

  methods: {
    ...mapActions({
      sendFileShare: 'fileShare/sendFileShare',
      revokeFileShareEmail: 'fileShare/revokeFileShareEmail'
    }),
    closeModal() {
      this.clear()
      this.showModal = false
      this.$emit('close')
    },
    clearShareFileEmails() {
      this.form.$reset()
    },
    clearShareFileErrrorEmails() {
      this.invalidTags = []
      this.maxTags = []
      this.tagErrors = {
        message: null,
        error_code: null
      }
    },
    clear() {
      this.clearShareFileEmails()
      this.clearShareFileErrrorEmails()
    },
    checkInvalidTag(tag) {
      return this.invalidTags.includes(tag)
    },
    checkExcessTag(tag) {
      return this.maxTags.includes(tag)
    },
    async submitFileShare() {
      this.loading = true
      this.form.$busy = true
      this.clearShareFileErrrorEmails()

      let data = {
        formData: this.form.$data(),
        fileId: this.selectedFiles[0]
      }

      await this.sendFileShare(data)
        .then(res => {
          if (res.data.invalid.length) {
            this.invalidTags = res.data.invalid
            this.tagErrors.message =
              'Unable to share files to the highlighted email addresses. They are not registered Business Admin Users on our platform. Please remove to proceed'
          }

          if (res.data.max.length) {
            this.maxTags = res.data.max
            this.tagErrors.message =
              'Max file sharing capacity or Max Business Admin email address share limit'
          }

          if (!res.data.invalid.length && !res.data.max.length) {
            this.$emit('fileShareResponsePrompt', res)
            this.clear()
            this.closeModal()
          }
        })
        .catch(error => {
          this.form.$handleError(error)
          if (error.response.data.error_code === 'NEED_TO_SUBSCRIBE') {
            this.showSubscribeModal = true
          } else {
            this.tagErrors.error_code = error.response.data.error_code
            this.tagErrors.message = error.response.data.message

            this.$emit('fileShareResponsePrompt', error.response)
          }
        })
        .finally(() => {
          this.loading = false
          this.form.$busy = false
        })
    },
    async currentlySharedRevoke(email) {
      this.loading = true
      this.clearShareFileErrrorEmails()

      let formData = new FormData()
      formData.append(`email`, email)

      let data = {
        formData: formData,
        fileId: this.selectedFiles[0]
      }

      await this.revokeFileShareEmail(data)
        .then(() => {
          remove(this.currentlySharedWith, e => {
            return e === email
          })

          this.$emit('reloadFileList')
        })
        .catch(error => {
          this.tagErrors.error_code = error.response.data.error_code
          this.tagErrors.message = error.response.data.message

          this.$emit('fileShareResponsePrompt', error.response)
        })
        .finally(() => {
          this.loading = false
        })
    }
  },

  watch: {
    fileShares(newVal, oldVal) {
      if (newVal.length) {
        each(newVal, sharedFile => {
          this.currentlySharedWith.push(sharedFile.shared_to.email)
        })
      } else {
        this.currentlySharedWith = []
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.badge-secondary {
  color: #364052;
  background: #e8eaed;
}

/deep/ .border-gray {
  border-color: #e5e5e5;
}

/deep/ .b-form-tags-button {
  display: none;
}
</style>
